import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import subscriptionRepository from "@/services/SubscriptionRepository";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { userInfoRepository } from "@/services/userInfoRepository";
import { useDeviceInfo } from "@/hooks/useDeviceInfo";
import { CONFIG } from "@/config";
import { UserSubscriptionDomain } from "@/types/UserSubscription";
import { TBankRecurentStatusDomain } from "@/types/TBankRecurentStatus";

export interface SubscriptionPlanType {
  title: string;
  price: string | null;
  questions: string;
  description: string;
  duration: number;
}

export interface SubscriptionPlansType {
  month_price: SubscriptionPlanType;
  month_3_price: SubscriptionPlanType;
  month_6_price: SubscriptionPlanType;
  year_price: SubscriptionPlanType;
}

export const SubscriptionPlans: SubscriptionPlansType = {
  
  month_price: {
    title: "1 месяц",
    price: null,
    questions: "-",
    description: "каждый месяц",
    duration: 1,
  },

  month_3_price: {
    title: "3 месяца",
    price: null,
    questions: "1 вопрос",
    description: "каждые 3 месяца",
    duration: 3,
  },

  month_6_price: {
    title: "6 месяцев",
    price: null,
    questions: "2 вопроса",
    description: "каждые 6 месяцев",
    duration: 6,
  },
  year_price: {
    title: "1 год",
    price: null,
    questions: "4 вопроса",
    description: "каждый год",
    duration: 12,
  },
};

export interface SubscriptionScreenViewModel {
  navigateBack: () => void;
  isConditionsOpen: boolean;
  setIsConditionsOpen: (isOpen: boolean) => void;
  onSelectPlan: (
    isAutoContinuation: boolean,
    subscriptionPlan: SubscriptionPlanType
  ) => void;
  plans: SubscriptionPlansType;
  paymentUrl: string | null;
  setPaymentUrl: (url: string | null) => void;
  openOffertsUrl: () => void;
  isPremium: boolean;
  isLoading: boolean;
  currentSubscription: UserSubscriptionDomain | null;
  isRecurrent: boolean;
  cancelRecurrent: () => void;
}

export const useSubscriptionScreenViewModel =
  (): SubscriptionScreenViewModel => {
    const [isPremium, setIsPremium] = useState(false);
    const [isConditionsOpen, setIsConditionsOpen] = useState(false);
    const [currentSubscription, setCurrentSubscription] = useState<UserSubscriptionDomain | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRecurrent, setIsRecurrent] = useState(false);
    const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
    const [plans, setPlans] = useState<SubscriptionPlansType>();
    const { getConfig } = userInfoRepository;
    const deviceInfo = useDeviceInfo();
    const [tBankPaymentRecurrent, setTBankPaymentRecurrent] = useState<TBankRecurentStatusDomain | null>(null);
    const navigate = useNavigate();
    const navigateBack = () => {
      navigate(-1);
    };

    const { subscription, isLoading: subscriptionLoading , refreshSubscription} = useSubscription();

    const isSubscribed = () => {
      if (subscriptionLoading) {
        return;
      }

      if (subscription?.isSubscribed) {
        setIsPremium(true);
        updateTBankPaymentRecurrent();
        tBankPaymentRecurrent// TODO не забыть использовать это
        setCurrentSubscription(subscription);
      }
    };

    const cancelRecurrent = async () => {
      try {
        const status = await subscriptionRepository.cancelRecurrent();
        if (status === 200) {
          setIsRecurrent(false);
          refreshSubscription();
        }
      } catch (error) {
        console.error(error);
      }
    };

    const updateTBankPaymentRecurrent = async () => {
      try {
        const tBankPaymentRecurrent = await subscriptionRepository.getTBankPaymentRecurrent();
        setTBankPaymentRecurrent(tBankPaymentRecurrent);
        setIsRecurrent(tBankPaymentRecurrent !== null);
      } catch (error) {
        console.error(error);
      }
    };

    const onSelectPlan = async (
      isAutoContinuation: boolean,
      subscriptionPlan: SubscriptionPlanType
    ) => {
      const paymentUrl = await subscriptionRepository.getSubscription(
        subscriptionPlan.duration,
        isAutoContinuation
      );
      if (paymentUrl) {
        if (
          deviceInfo.isIOS &&
          deviceInfo.isMobile &&
          deviceInfo.isStandalone || deviceInfo.isIPad
        ) {
          setPaymentUrl(paymentUrl);
        } else {
          window.location.href = paymentUrl ?? "";
        }
      }
    };

    const initPlans = async () => {
      const config = await getConfig();
      const oneMonthPrice = config?.monthPrice;
      const threeMonthPrice = config?.month3Price;
      const sixMonthPrice = config?.month6Price;
      const yearPrice = config?.yearPrice;

      const plans: SubscriptionPlansType = {
        month_price: {
          ...SubscriptionPlans.month_price,
          price: oneMonthPrice ?? null,
        },
        month_3_price: {
          ...SubscriptionPlans.month_3_price,
          price: threeMonthPrice ?? null,
        },
        month_6_price: {
          ...SubscriptionPlans.month_6_price,
          price: sixMonthPrice ?? null,
        },
        year_price: {
          ...SubscriptionPlans.year_price,
          price: yearPrice ?? null,
        },
      };
      setPlans(plans);
    };

    const openOffertsUrl = () => {
      const offertsUrl = CONFIG.links.subscriptionOffer;
      if (offertsUrl) {
        window.open(offertsUrl, "_blank");
      }
    };

    useEffect(() => {
      setIsLoading(true);
      isSubscribed();
      initPlans();
      setIsLoading(false);
    }, [subscription, subscriptionLoading]);

    return {
      paymentUrl,
      setPaymentUrl,
      navigateBack,
      isConditionsOpen,
      setIsConditionsOpen,
      onSelectPlan,
      plans: plans ?? SubscriptionPlans,
      openOffertsUrl,
      isPremium,
      isLoading,
      currentSubscription,
      isRecurrent,
      cancelRecurrent,
    };
  };
